import { authService } from '@/features/auth/auth-service'
import { ApiError } from '@/lib/api/api-error'
import type {
  ApiAuthErrorResponse,
  ApiResponse,
  PaginatedApiSuccessResponse,
  PaginatedResult,
  PaginatedResultWithSummary,
} from '@/lib/api/types'

type RequestPayload<TData> =
  | ApiResponse<TData>
  | PaginatedApiSuccessResponse<TData>
  | ApiAuthErrorResponse

const getErrorMessage = (
  payload: RequestPayload<unknown>,
  status: number
): string => {
  if ('success' in payload && payload.success === false) {
    return payload.message ?? payload.error ?? `Request failed (${status})`
  }

  if ('error' in payload && payload.error) {
    return payload.error
  }

  return `Request failed (${status})`
}

const toApiError = (
  payload: RequestPayload<unknown>,
  status: number
): ApiError => {
  const errors =
    'success' in payload && payload.success === false ? payload.errors : undefined

  return new ApiError(getErrorMessage(payload, status), { status, errors })
}

const isSuccessPayload = <TData>(
  payload: RequestPayload<TData>
): payload is ApiResponse<TData> & { success: true; data: TData } =>
  'success' in payload && payload.success === true && 'data' in payload

const isPaginatedSuccessPayload = <TData>(
  payload: RequestPayload<TData>
): payload is PaginatedApiSuccessResponse<TData> =>
  'success' in payload &&
  payload.success === true &&
  'data' in payload &&
  'pagination' in payload

const isPaginatedSuccessPayloadWithSummary = <TData, TSummary>(
  payload: RequestPayload<TData>
): payload is PaginatedApiSuccessResponse<TData> & { summary: TSummary } =>
  isPaginatedSuccessPayload(payload) &&
  'summary' in payload &&
  payload.summary !== undefined

const request = async <TData>(path: string, init: RequestInit = {}) => {
  const token = await authService.acquireAccessToken()
  const { headers: initHeaders, body, method = 'GET', ...restInit } = init

  const headers = new Headers(initHeaders)
  headers.set('Authorization', `Bearer ${token}`)
  headers.set('Accept', 'application/json')

  if (body !== undefined && body !== null) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(path, {
    ...restInit,
    method,
    body,
    headers,
  })

  const payload = (await response.json()) as RequestPayload<TData>

  return { response, payload }
}

export const apiClient = async <TData>(
  path: string,
  init: RequestInit = {}
): Promise<TData> => {
  const { response, payload } = await request<TData>(path, init)

  if (!response.ok || !isSuccessPayload(payload)) {
    throw toApiError(payload, response.status)
  }

  return payload.data
}

export const apiPaginatedClient = async <TData>(
  path: string,
  init: RequestInit = {}
): Promise<PaginatedResult<TData>> => {
  const { response, payload } = await request<TData>(path, init)

  if (!response.ok || !isPaginatedSuccessPayload(payload)) {
    throw toApiError(payload, response.status)
  }

  return {
    data: payload.data,
    pagination: payload.pagination,
  }
}

export const apiPaginatedClientWithSummary = async <TData, TSummary>(
  path: string,
  init: RequestInit = {}
): Promise<PaginatedResultWithSummary<TData, TSummary>> => {
  const { response, payload } = await request<TData>(path, init)

  if (!response.ok || !isPaginatedSuccessPayloadWithSummary<TData, TSummary>(payload)) {
    throw toApiError(payload, response.status)
  }

  return {
    data: payload.data,
    pagination: payload.pagination,
    summary: payload.summary,
  }
}
